import { LocalAudioTrack } from 'livekit-client';
import React, {
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import Hls from 'hls.js';
import {
  getStore,
  usePlayerStore,
  useExamSessionStore,
} from '../../../stores/StoreContext';
import { View } from '@instructure/ui';
import { Parser } from 'm3u8-parser';
import moment from 'moment';
import CONSTANTS from '../../../config/constants';
import { createSignedUrlLoader } from './SignedUrlLoader';

const HlsVideoPlayer = forwardRef(
  (
    { url, startTime = 0, endTime, onEndTime, hlsConfig = {}, videoProps = {}, showRawVideo = false, onBufferingStart, onBufferingEnd, onPlaying, onSuggestedSpeedChange },
    ref
  ) => {
    const videoRef = useRef(null);
    const hlsInstanceRef = useRef(null);

    // Refs to avoid stale closures in interval callbacks (which capture values at mount time)
    const playbackSpeedRef = useRef(1);
    const playingRef = useRef(false);
    const smartSpeedEnabledRef = useRef(false);
    const seekBufferPrimingRef = useRef(false); // True when seekBufferPrime is actively holding playback

    const PlayerStore = getStore('playerStore');
    const ExamSessionStore = getStore('examSessionStore');

    const [audioTrack, setAudioTrack] = useState(null);

    const playbackSpeed = usePlayerStore((state) => state.playbackSpeed);
    const smartSpeedEnabled = usePlayerStore((state) => state.smartSpeedEnabled);
    const currentPlaybackTime = usePlayerStore((state) => state.currentTime);
    const playing = usePlayerStore((state) => state.playing);
    const recordingStatus = useExamSessionStore(
      (state) => state.recordingStatus
    );

    const AppStore = getStore('appStore');

    // Keep refs in sync with latest state to avoid stale closures
    useEffect(() => { playbackSpeedRef.current = playbackSpeed; }, [playbackSpeed]);
    useEffect(() => { playingRef.current = playing; }, [playing]);
    useEffect(() => { smartSpeedEnabledRef.current = smartSpeedEnabled; }, [smartSpeedEnabled]);

    /**
     * Get the effective playback speed for buffer calculations.
     * Smart speed (100.0) is treated as DEFAULT_SMART_SPEED_PLAYBACK_SPEED (4x)
     * for all buffer/prefetch decisions since 100.0 is just a sentinel value.
     * @returns {number} The actual speed multiplier to use for calculations
     */
    const getEffectiveSpeed = () => {
      const speed = playbackSpeedRef.current;
      const numericSpeed = typeof speed === 'string' ? parseFloat(speed) : speed;
      // Smart speed uses 100.0 as sentinel — treat as 10x for aggressive buffering
      if (numericSpeed >= 100) return 10;
      return numericSpeed;
    };

    // Manifest response handler — preserves the original RawLoader behavior
    // for updating recording end dates on live/in-progress sessions.
    const handleManifestResponse = (manifestData) => {
      if (recordingStatus !== CONSTANTS.RECORDING_STATUS.COMPLETE) {
        var parser = new Parser();
        parser.push(manifestData);
        parser.end();

        var parsedManifest = parser.manifest;

        // update the recording end date if the HLS file is still being updated...
        if (!parsedManifest.endList) {
          const lastSegment =
            parsedManifest.segments[parsedManifest.segments.length - 1];
          const recordingEndDate = moment
            .utc(lastSegment.dateTimeString)
            .add(lastSegment.duration, 'seconds')
            .toISOString();
          ExamSessionStore.setRecordingMetadata({
            endDate: recordingEndDate,
          });
        }
      }
    };

    // Create the signed URL loader that intercepts segment requests to fetch
    // signed S3 CDN URLs from the backend before loading each segment.
    const SignedUrlLoader = createSignedUrlLoader({
      getApiClient: () => AppStore.ApiClient,
      getSessionParams: () => {
        const examSession = ExamSessionStore;
        return {
          installSid: examSession.installSid,
          examSid: examSession.examSid,
          sessionSid: examSession.sessionSid,
        };
      },
      onManifestResponse: handleManifestResponse,
      cacheOptions: {
        maxSize: 500,
        ttlMs: 5 * 60 * 1000, // 5 minute TTL for signed URLs
      },
    });

    const isPlaying = () => {
      const video = videoRef.current;
      if (!video) {
        return false;
      }
      if (!video.paused && !video.ended && video.readyState > 2) {
        return true;
      } else {
        return false;
      }
    };

    useImperativeHandle(
      ref,
      () => ({
        play: () => videoRef.current?.play(),
        pause: () => videoRef.current?.pause(),
        setPlaybackRate: (rate) => {
          if (videoRef.current) {
            videoRef.current.playbackRate = rate;
          }
        },
        loadSource: (newUrl) => {
          if (Hls.isSupported() && hlsInstanceRef.current) {
            hlsInstanceRef.current.loadSource(newUrl);
          } else if (videoRef.current) {
            videoRef.current.src = newUrl;
          }
        },
        // Expose the hls.js instance
        getHlsInstance: () => hlsInstanceRef.current,
      }),
      []
    );

    useEffect(() => {
      const video = videoRef.current;

      // Validate URL before attempting to load
      if (!url || url.includes('undefined') || url.includes('null')) {
        console.error('HlsVideoPlayer: Invalid URL provided:', url);
        return;
      }

      // Set the HLS Video in the player store so it can be used in other places...
      PlayerStore.setHlsVideoRef(videoRef);

      // Local throttle for timeupdate — avoids calling isPlaying()/store on every browser event
      let lastTimeUpdateMs = 0;
      const handleTimeUpdate = () => {
        // Guard against NaN currentTime which can occur after hls.stopLoad()/startLoad() transitions
        if (!isFinite(video.currentTime)) return;

        // Throttle based on effective speed: 10x→100ms, 5x→200ms, 2x→500ms, 1x→1000ms
        const now = performance.now();
        const speed = getEffectiveSpeed();
        const throttleMs = speed >= 10 ? 100 : speed >= 5 ? 200 : speed >= 2 ? 500 : 1000;
        if (now - lastTimeUpdateMs < throttleMs) return;
        lastTimeUpdateMs = now;

        const currentSecond = Math.floor(video.currentTime);
        if (isPlaying()) {
          PlayerStore.setCurrentTime(currentSecond);
        }
      };

      /**
       * Handle video ended event - ensures final time is set even at high playback speeds
       * This fires when the video naturally ends, regardless of timeupdate frequency
       */
      const handleEnded = () => {
        const videoDuration = Math.ceil(video.duration);

        PlayerStore.setPlaying(false);
        PlayerStore.setCurrentTime(videoDuration); // Set to exact duration
        video.pause();
        if (onEndTime) {
          onEndTime();
        }
      };

      // Hoisted here so handlePlaying (defined before Hls.isSupported block) can access it
      let playbackStartedAt = 0;

      /**
       * Handle seeked event - fires when seeking operation completes
       * Clear the seeking state when video is ready to play at new position
       */
      let seekBufferPrimeTimer = null;
      const handleSeeked = () => {
        // Always clear seeking state when seeked event fires
        PlayerStore.setIsSeeking(false);

        // At high speeds, briefly hold playback after seek to let buffer prime
        // This prevents the immediate stall that monitorBuffer would trigger
        // (at 10x, monitorBuffer needs 50s min buffer which won't exist right after seek)
        const speed = getEffectiveSpeed();
        // Use playingRef (store intent) not !video.paused (element state).
        // jumpTo() pauses the video before seeking, so video.paused is true at seeked time,
        // but the user intends to play. playingRef captures this intent.
        if (speed >= 4 && playingRef.current && video.duration > 0) {
          const buffered = video.buffered;
          let bufferAhead = 0;
          for (let i = 0; i < buffered.length; i++) {
            if (buffered.start(i) <= video.currentTime && buffered.end(i) > video.currentTime) {
              bufferAhead = buffered.end(i) - video.currentTime;
              break;
            }
          }

          // Only prime if buffer is low relative to speed needs.
          // Threshold: 1 viewing-second worth of buffer (speed × 1), min 3s raw.
          // At 10x: need 10s raw buffer. Seek prime is just to avoid the immediate stall;
          // monitorBuffer handles long-term buffer health after its grace period.
          const neededBuffer = Math.max(3, speed * 1);
          if (bufferAhead < neededBuffer) {
            video.pause();
            seekBufferPrimingRef.current = true;
            if (onBufferingStart) {
              onBufferingStart({ bufferAhead, speed, targetBuffer: neededBuffer });
            }

            // Check every 200ms if we have enough buffer to resume (max 5s wait)
            if (seekBufferPrimeTimer) clearInterval(seekBufferPrimeTimer);
            const seekPrimeStartTime = performance.now();
            const SEEK_PRIME_MAX_WAIT_MS = 5000;
            seekBufferPrimeTimer = setInterval(() => {
              let currentBuffer = 0;
              for (let i = 0; i < video.buffered.length; i++) {
                if (video.buffered.start(i) <= video.currentTime && video.buffered.end(i) > video.currentTime) {
                  currentBuffer = video.buffered.end(i) - video.currentTime;
                  break;
                }
              }

              const timedOut = performance.now() - seekPrimeStartTime > SEEK_PRIME_MAX_WAIT_MS;
              if (currentBuffer >= neededBuffer || timedOut) {
                clearInterval(seekBufferPrimeTimer);
                seekBufferPrimeTimer = null;
                seekBufferPrimingRef.current = false;
                // Set cooldown + grace so monitorBuffer doesn't immediately re-pause
                bufferResumeCooldownUntil = performance.now() + BUFFER_RESUME_COOLDOWN_MS;
                playbackStartedAt = performance.now();
                if (playingRef.current) {
                  video.play().catch(err => console.error('Failed to resume after seek prime:', err));
                }
                if (onBufferingEnd) {
                  onBufferingEnd({ bufferAhead: currentBuffer, speed });
                }
                if (timedOut) {
                  console.warn(`[Seek Prime] Timed out after ${SEEK_PRIME_MAX_WAIT_MS}ms with ${currentBuffer.toFixed(1)}s buffer (needed ${neededBuffer}s). Resuming anyway.`);
                }
              }
            }, 200);
            return; // Seek prime is managing playback — don't fall through to resume below
          }
        }

        // If we didn't activate seek prime (speed < 4x, or buffer was already sufficient),
        // resume playback if the user intends to play. This handles jumpTo() which pauses
        // the video before seeking — without this, the video would stay paused forever
        // since setPlaying(true) may be a no-op if already playing.
        if (playingRef.current && video.paused) {
          video.play().catch(err => console.error('Failed to resume after seek:', err));
        }
      };

      /**
       * Handle playing event - fires when video actually starts playing
       * This is critical for clearing buffer overlays that may be stuck
       */
      const handlePlaying = () => {
        // Record when playback started so monitorBuffer observes a grace period
        playbackStartedAt = performance.now();

        if (onPlaying) {
          onPlaying({
            currentTime: video.currentTime,
            playbackRate: video.playbackRate
          });
        }
      };

      if (Hls.isSupported()) {
        // Base buffer values — these are dynamically scaled by the [playbackSpeed] effect
        const finalConfig = {
          startPosition: startTime,
          loader: SignedUrlLoader,

          // Buffer lengths (initial values for 1x; dynamically adjusted when speed changes)
          maxBufferLength: 30,              // HLS.js guaranteed minimum buffer target
          maxMaxBufferLength: 60,           // Hard cap on buffer length
          maxBufferSize: 100 * 1000 * 1000, // 100MB buffer size limit
          backBufferLength: 30,             // Keep 30s behind for seek-back at normal speed
          frontBufferFlushThreshold: 60,    // Flush forward buffer beyond this

          // Gap and stall recovery
          maxBufferHole: 0.3,              // Tolerate up to 300ms gaps between fragments
          highBufferWatchdogPeriod: 2,     // Detect stalls faster (default 3s)
          nudgeMaxRetry: 5,                // More nudge retries before fatal error (default 3)
          nudgeOffset: 0.2,                // Larger nudge steps for faster recovery
          maxStarvationDelay: 2,           // Reduce ABR starvation delay (default 4s)

          // Fragment loading optimization
          maxFragLookUpTolerance: 0.25,
          maxLoadingDelay: 2,               // Reduce initial load delay to 2s
          fragLoadingTimeOut: 20000,        // 20s timeout
          fragLoadingMaxRetry: 6,
          fragLoadingMaxRetryTimeout: 64000,

          // Enable prefetching
          startFragPrefetch: true,          // Start prefetching next fragment early
          testBandwidth: true,              // Test bandwidth for optimization

          // ABR optimization for quality
          abrEwmaDefaultEstimate: 5000000,  // 5Mbps default estimate
          abrBandWidthFactor: 0.95,
          abrBandWidthUpFactor: 0.7,
          abrMaxWithRealBitrate: true,      // Use measured bitrate for more accurate ABR
          abrEwmaFastVoD: 2.0,             // Faster EWMA response for VoD (default 3.0)
          abrEwmaSlowVoD: 6.0,             // Faster slow EWMA for VoD (default 9.0)

          // Performance
          enableWorker: true,               // Use web worker for demuxing
          lowLatencyMode: false,            // Prioritize buffer over latency
          progressive: true,                // Stream segment data with fetch loader

          // Auto-start
          autoStartLoad: true,
          startLevel: -1,                   // Auto-select quality

          ...hlsConfig,
        };
        const hls = new Hls(finalConfig);
        hlsInstanceRef.current = hls;
        // Register hls.js instance in PlayerStore for stopLoad/startLoad control
        PlayerStore.setHlsInstanceRef(hls);
        // Hide all video controls
        video.controls = false;

        hls.loadSource(url);
        hls.attachMedia(video);

        // If WebRTC is currently active, detach media immediately after initial attach
        // This prevents segment downloads when the proctor is watching live video
        // detachMedia() is used instead of stopLoad() because stopLoad() leaves hls.js
        // in an inconsistent state that prevents proper resumption via startLoad()
        if (PlayerStore.activeVideoSource === CONSTANTS.VIDEO_SOURCES.LIVE) {
          hls.detachMedia();
        }

        // Coordinated prefetch system
        let prefetchInterval = null;
        const hlsLoadingSegments = new Set(); // Track what HLS.js is loading
        const prefetchedSegments = new Set(); // Track what we've prefetched
        const activePrefetches = new Set(); // Track active prefetch requests
        // MAX_CONCURRENT is now dynamic — computed inside coordinatedPrefetch based on speed
        let totalSegments = null; // Track total segments from manifest

        // Buffer monitoring for pause/resume
        let bufferMonitorInterval = null;
        let isBuffering = false;
        let wasPlayingBeforeBuffer = false;
        let bufferResumeCooldownUntil = 0; // Prevents immediate re-trigger after resume
        const BUFFER_CHECK_INTERVAL = 500; // Check buffer every 500ms
        const BUFFER_RESUME_COOLDOWN_MS = 3000; // 3s cooldown after resuming from buffer pause
        const PLAYBACK_GRACE_PERIOD_MS = 5000; // 5s grace after play starts before buffer monitor intervenes
        // Buffer thresholds in "viewing seconds" (not raw buffer seconds):
        // MIN_VIEWING_SECONDS: pause if buffer provides less than this many seconds of viewing
        // TARGET_VIEWING_SECONDS: resume when buffer provides this many seconds of viewing
        // Keep these LOW so the buffer overlay resolves quickly (a few seconds, not 20+).
        const MIN_VIEWING_SECONDS = 1.5;  // 1.5s of viewing = 15s raw buffer at 10x
        const TARGET_VIEWING_SECONDS = 3; // 3s of viewing = 30s raw buffer at 10x

        // Network performance tracking for dynamic speed suggestions
        const segmentLoadTimes = []; // Track recent segment load times
        const MAX_LOAD_TIME_SAMPLES = 10; // Keep last 10 segment load times
        let lastBufferCheck = { time: 0, bufferAhead: 0 };
        let currentSuggestedSpeed = null;
        let speedSuggestionInterval = null;

        // Track HLS.js fragment loading to avoid conflicts
        hls.on(Hls.Events.FRAG_LOADING, (event, data) => {
          if (data.frag && data.frag.url) {
            hlsLoadingSegments.add(data.frag.url);
            // Track load start time
            data.frag.loadStartTime = performance.now();
          }
        });

        hls.on(Hls.Events.FRAG_LOADED, (event, data) => {
          if (data.frag && data.frag.url) {
            hlsLoadingSegments.delete(data.frag.url);

            // Track segment load time for performance analysis
            if (data.frag.loadStartTime) {
              const loadTime = performance.now() - data.frag.loadStartTime;
              segmentLoadTimes.push(loadTime);

              // Keep only recent samples
              if (segmentLoadTimes.length > MAX_LOAD_TIME_SAMPLES) {
                segmentLoadTimes.shift();
              }
            }
          }
        });

        const calculateOptimalSpeed = () => {
          // Skip when paused or when WebRTC is the active video source
          if (!playingRef.current) return;
          if (PlayerStore.activeVideoSource === CONSTANTS.VIDEO_SOURCES.LIVE) return;

          const currentSpeed = getEffectiveSpeed();

          // Don't suggest speeds if we don't have enough data yet
          if (segmentLoadTimes.length < 3 || !video.duration) return;

          const currentTime = video.currentTime;
          const buffered = video.buffered;

          // Calculate current buffer ahead
          let bufferAhead = 0;
          if (buffered.length > 0) {
            for (let i = 0; i < buffered.length; i++) {
              if (buffered.start(i) <= currentTime && buffered.end(i) > currentTime) {
                bufferAhead = buffered.end(i) - currentTime;
                break;
              }
            }
          }

          // Calculate average segment load time (in ms)
          const avgLoadTime = segmentLoadTimes.reduce((a, b) => a + b, 0) / segmentLoadTimes.length;
          const segmentDuration = 5000; // 5 seconds in ms

          // Calculate buffer growth rate (how fast buffer is growing/shrinking)
          const now = performance.now();
          let bufferGrowthRate = 0;
          if (lastBufferCheck.time > 0) {
            const timeDelta = (now - lastBufferCheck.time) / 1000; // Convert to seconds
            const bufferDelta = bufferAhead - lastBufferCheck.bufferAhead;
            bufferGrowthRate = bufferDelta / timeDelta; // Seconds of buffer per second
          }
          lastBufferCheck = { time: now, bufferAhead };

          // Calculate network capacity: how many seconds of video we can download per second
          // If a 5-second segment takes 500ms to download, we can download 10 seconds of video per second
          const downloadCapacity = segmentDuration / avgLoadTime;

          // Calculate sustainable speed based on network capacity
          // We want to leave some headroom (80% of capacity) to avoid buffering
          const networkBasedSpeed = Math.floor(downloadCapacity * 0.8);

          // Factor in buffer health
          let bufferBasedSpeed;
          if (bufferAhead > 90) {
            // Excellent buffer - can go max speed
            bufferBasedSpeed = 16;
          } else if (bufferAhead > 60) {
            // Good buffer - can go high speed
            bufferBasedSpeed = 12;
          } else if (bufferAhead > 40) {
            // Moderate buffer - medium-high speed
            bufferBasedSpeed = 10;
          } else if (bufferAhead > 30) {
            // Low buffer - reduce speed
            bufferBasedSpeed = 8;
          } else {
            // Critical buffer - slow down significantly
            bufferBasedSpeed = 4;
          }

          // Factor in buffer growth rate
          let growthBasedSpeed;
          if (bufferGrowthRate > 2) {
            // Buffer growing fast - can increase speed
            growthBasedSpeed = 16;
          } else if (bufferGrowthRate > 0.5) {
            // Buffer growing - maintain or increase
            growthBasedSpeed = 12;
          } else if (bufferGrowthRate > -0.5) {
            // Buffer stable - maintain current speed
            growthBasedSpeed = currentSpeed;
          } else if (bufferGrowthRate > -2) {
            // Buffer shrinking - reduce speed
            growthBasedSpeed = Math.max(4, currentSpeed - 2);
          } else {
            // Buffer shrinking fast - reduce significantly
            growthBasedSpeed = 4;
          }

          // Take the minimum of all factors to be conservative
          let suggestedSpeed = Math.min(networkBasedSpeed, bufferBasedSpeed, growthBasedSpeed);

          // Clamp to valid range (1x to 16x)
          suggestedSpeed = Math.max(1, Math.min(16, suggestedSpeed));

          // Round to common speeds: 1, 2, 4, 8, 10, 12, 16
          const commonSpeeds = [1, 2, 4, 8, 10, 12, 16];
          suggestedSpeed = commonSpeeds.reduce((prev, curr) =>
            Math.abs(curr - suggestedSpeed) < Math.abs(prev - suggestedSpeed) ? curr : prev
          );

          // Only notify if speed suggestion changed
          if (suggestedSpeed !== currentSuggestedSpeed) {
            const previousSuggestion = currentSuggestedSpeed;
            currentSuggestedSpeed = suggestedSpeed;

            // Call callback if provided
            if (onSuggestedSpeedChange) {
              onSuggestedSpeedChange({
                suggestedSpeed,
                currentSpeed,
                previousSuggestion,
                metrics: {
                  bufferAhead,
                  bufferGrowthRate,
                  avgLoadTime,
                  downloadCapacity: downloadCapacity.toFixed(1),
                  networkBasedSpeed,
                  bufferBasedSpeed,
                  growthBasedSpeed
                }
              });
            }
          }
        };

        const monitorBuffer = () => {
          // Skip when paused (unless we're mid-buffer-pause) or when WebRTC is active
          if (!playingRef.current && !isBuffering) return;
          if (PlayerStore.activeVideoSource === CONSTANTS.VIDEO_SOURCES.LIVE) return;

          // Don't trigger new buffer pause if seekBufferPrime is actively managing playback
          if (seekBufferPrimeTimer && !isBuffering) return;

          // Cooldown after a buffer resume — don't re-trigger within 3s
          if (!isBuffering && performance.now() < bufferResumeCooldownUntil) return;

          // Grace period — don't trigger buffer monitor within 5s of playback starting.
          // Gives HLS.js time to fill the buffer naturally before we intervene.
          if (!isBuffering && playbackStartedAt > 0 && performance.now() - playbackStartedAt < PLAYBACK_GRACE_PERIOD_MS) return;

          const speed = getEffectiveSpeed();

          // Only monitor at speeds >= 4x where the browser can genuinely fall behind.
          // At 2-3x, HLS.js can naturally keep up with playback.
          if (speed < 4 || !video.duration) {
            return;
          }

          const currentTime = video.currentTime;
          const buffered = video.buffered;
          const remainingTime = video.duration - currentTime;

          // Calculate available buffer ahead
          let bufferAhead = 0;
          if (buffered.length > 0) {
            for (let i = 0; i < buffered.length; i++) {
              if (buffered.start(i) <= currentTime && buffered.end(i) > currentTime) {
                bufferAhead = buffered.end(i) - currentTime;
                break;
              }
            }
          }

          // Thresholds in "viewing seconds" converted to raw buffer seconds.
          // MIN_VIEWING_SECONDS=1.5 → at 10x, need 15s raw buffer (1.5s of viewing) before pausing
          // TARGET_VIEWING_SECONDS=3 → at 10x, need 30s raw buffer (3s of viewing) before resuming
          // Capped so we never require more than 30s/60s raw buffer regardless of speed.
          const scaledMinBuffer = Math.min(30, speed * MIN_VIEWING_SECONDS);
          const scaledTargetBuffer = Math.min(60, speed * TARGET_VIEWING_SECONDS);

          // Adjust buffer requirements if we're near the end of the video
          const effectiveMinBuffer = Math.min(scaledMinBuffer, remainingTime);
          const effectiveTargetBuffer = Math.min(scaledTargetBuffer, remainingTime);

          // Check if we need to start buffering
          // Don't buffer if we're within 10 seconds of the end (at any speed)
          if (!isBuffering && bufferAhead < effectiveMinBuffer && !video.paused && remainingTime > 10) {
            isBuffering = true;
            wasPlayingBeforeBuffer = !video.paused;

            // Pause video
            video.pause();

            // Call user's callback for showing overlay
            if (onBufferingStart) {
              onBufferingStart({ bufferAhead, speed, targetBuffer: effectiveTargetBuffer });
            }
          }

          // Check if we can resume playback
          if (isBuffering && bufferAhead >= effectiveTargetBuffer) {
            isBuffering = false;
            bufferResumeCooldownUntil = performance.now() + BUFFER_RESUME_COOLDOWN_MS;

            // Resume if we were playing before
            if (wasPlayingBeforeBuffer) {
              video.play().catch(err => console.error('Failed to resume:', err));
            }

            // Call user's callback for hiding overlay
            if (onBufferingEnd) {
              onBufferingEnd({ bufferAhead, speed });
            }
          }
        };

        const coordinatedPrefetch = () => {
          // Skip when paused or when WebRTC is the active video source
          if (!playingRef.current) return;
          if (PlayerStore.activeVideoSource === CONSTANTS.VIDEO_SOURCES.LIVE) return;

          const speed = getEffectiveSpeed();

          // Only prefetch at speeds above 2x (lowered from 8x so smart speed triggers)
          if (speed < 2 || !video.duration || !totalSegments) return;

          const currentTime = video.currentTime;
          const segmentDuration = 5;
          const currentSeg = Math.floor(currentTime / segmentDuration);

          // Find where HLS.js buffer ends
          const buffered = video.buffered;
          let hlsBufferEnd = currentTime;
          if (buffered.length > 0) {
            for (let i = 0; i < buffered.length; i++) {
              if (buffered.start(i) <= currentTime && buffered.end(i) > currentTime) {
                hlsBufferEnd = buffered.end(i);
                break;
              }
            }
          }

          const hlsBufferEndSeg = Math.floor(hlsBufferEnd / segmentDuration);

          // Start prefetching well beyond HLS.js buffer
          // Add 10 segment safety margin to avoid conflicts with HLS.js native loading
          const prefetchStartSeg = Math.max(hlsBufferEndSeg + 10, currentSeg + 20);

          // Don't prefetch if we're already near the end
          if (prefetchStartSeg >= totalSegments) {
            return;
          }

          // Prefetch count scales with speed: min(60, round(speed × 5))
          const rawPrefetchCount = Math.min(60, Math.round(speed * 5));
          const prefetchCount = Math.min(rawPrefetchCount, totalSegments - prefetchStartSeg);

          const levels = hls.levels;
          if (!levels || levels.length === 0) return;

          const levelUrl = levels[hls.currentLevel]?.url?.[0] || levels[0]?.url?.[0];
          if (!levelUrl) return;

          const baseUrl = levelUrl.substring(0, levelUrl.lastIndexOf('/') + 1);

          // Prefetch segments beyond HLS.js buffer
          let segmentsQueued = 0;
          for (let i = 0; i < prefetchCount; i++) {
            const segNum = prefetchStartSeg + i;

            // Double-check we haven't exceeded total segments
            if (segNum >= totalSegments) {
              break;
            }

            const segUrl = `${baseUrl}segment_${String(segNum).padStart(5, '0')}.ts`;

            // Skip if HLS.js is loading it, we've prefetched it, or we're prefetching it
            if (hlsLoadingSegments.has(segUrl) ||
              prefetchedSegments.has(segUrl) ||
              activePrefetches.has(segUrl)) {
              continue;
            }

            // Dynamic concurrent limit: scales with speed (2x=4, 5x=8, 10x=12)
            const maxConcurrent = Math.min(12, Math.max(4, Math.round(speed * 1.2)));
            if (activePrefetches.size >= maxConcurrent) break;

            activePrefetches.add(segUrl);
            segmentsQueued++;

            // Resolve signed URL before prefetching from CDN
            SignedUrlLoader.getSignedUrl(segUrl)
              .then((signedUrl) => fetch(signedUrl, { priority: 'low' }))
              .then(response => {
                if (response.ok) {
                  prefetchedSegments.add(segUrl);
                } else {
                  console.warn(`[Prefetch] Failed to load segment ${segNum}: ${response.status}`);
                }
                activePrefetches.delete(segUrl);

                // Limit cache size - remove oldest prefetched segments
                if (prefetchedSegments.size > 100) {
                  const first = prefetchedSegments.values().next().value;
                  prefetchedSegments.delete(first);
                }
              })
              .catch((err) => {
                console.warn(`[Prefetch] Error loading segment ${segNum}:`, err.message);
                activePrefetches.delete(segUrl);
              });
          }
        };

        const handleVideoReady = () => {
          PlayerStore.setHlsPlaybackReady(true);
        };

        hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
          try {
            //video.play();
          } catch (e) {
            console.warn(
              'Cannot autoplay video until user interacts with the document.'
            );
          }

          // Calculate total segments from manifest data (may not be available yet)
          if (data.levels && data.levels.length > 0) {
            const level = data.levels[0];
            if (level.details && level.details.fragments) {
              totalSegments = level.details.fragments.length;
              console.log(`[Manifest] Total segments from manifest: ${totalSegments}`);
            }
          }

          // Clear any existing intervals before starting new ones
          // (detachMedia/attachMedia triggers MANIFEST_PARSED again on re-attach)
          if (prefetchInterval) clearInterval(prefetchInterval);
          if (bufferMonitorInterval) clearInterval(bufferMonitorInterval);
          if (speedSuggestionInterval) clearInterval(speedSuggestionInterval);

          // Start coordinated prefetch - runs every 300ms
          prefetchInterval = setInterval(coordinatedPrefetch, 300);

          // Start buffer monitor - runs every 500ms
          bufferMonitorInterval = setInterval(monitorBuffer, BUFFER_CHECK_INTERVAL);

          // Start speed suggestion calculator - runs every 2 seconds
          speedSuggestionInterval = setInterval(calculateOptimalSpeed, 2000);
        });

        // Reliable fallback for totalSegments — LEVEL_LOADED fires when the actual
        // media playlist is parsed (MANIFEST_PARSED often fires before level details exist)
        hls.on(Hls.Events.LEVEL_LOADED, (event, data) => {
          if (!totalSegments && data.details && data.details.fragments) {
            totalSegments = data.details.fragments.length;
            console.log(`[Level Loaded] Total segments from level details: ${totalSegments} (duration: ${data.details.totalduration?.toFixed(1)}s)`);
          }
        });

        // Auto-recovery for fatal HLS errors — critical for smooth high-speed playback
        let mediaErrorRecoveryAttempts = 0;
        const MAX_MEDIA_RECOVERY_ATTEMPTS = 3;

        hls.on(Hls.Events.ERROR, (event, data) => {
          if (!data.fatal) return;

          switch (data.type) {
            case Hls.ErrorTypes.MEDIA_ERROR:
              // Media decode errors are common at high speeds — auto-recover
              if (mediaErrorRecoveryAttempts < MAX_MEDIA_RECOVERY_ATTEMPTS) {
                mediaErrorRecoveryAttempts++;
                console.warn(`[HLS] Fatal media error (attempt ${mediaErrorRecoveryAttempts}/${MAX_MEDIA_RECOVERY_ATTEMPTS}), recovering...`, data.details);

                if (mediaErrorRecoveryAttempts === 1) {
                  // First attempt: standard media error recovery
                  hls.recoverMediaError();
                } else {
                  // Subsequent attempts: swap audio codec and recover
                  hls.swapAudioCodec();
                  hls.recoverMediaError();
                }
              } else {
                console.error('[HLS] Fatal media error, max recovery attempts exceeded:', data);
              }
              break;

            case Hls.ErrorTypes.NETWORK_ERROR:
              // Network errors — attempt to restart loading from current position
              console.warn('[HLS] Fatal network error, attempting restart...', data.details);
              hls.startLoad();
              break;

            default:
              console.error('[HLS] Fatal error, cannot recover:', data);
              break;
          }
        });

        // Reset recovery counter on successful playback to allow future recovery
        hls.on(Hls.Events.FRAG_BUFFERED, () => {
          if (mediaErrorRecoveryAttempts > 0) {
            mediaErrorRecoveryAttempts = 0;
          }
        });

        video.addEventListener('timeupdate', handleTimeUpdate);
        video.addEventListener('ended', handleEnded);
        video.addEventListener('seeked', handleSeeked);
        video.addEventListener('playing', handlePlaying);
        video.addEventListener('loadeddata', handleVideoReady);
        video.addEventListener('loadedmetadata', handleVideoReady);
        video.addEventListener('canplay', handleVideoReady);

        return () => {
          video.removeEventListener('timeupdate', handleTimeUpdate);
          video.removeEventListener('ended', handleEnded);
          video.removeEventListener('seeked', handleSeeked);
          video.removeEventListener('playing', handlePlaying);
          video.removeEventListener('loadeddata', handleVideoReady);
          video.removeEventListener('loadedmetadata', handleVideoReady);
          video.removeEventListener('canplay', handleVideoReady);
          if (prefetchInterval) {
            clearInterval(prefetchInterval);
          }
          if (bufferMonitorInterval) {
            clearInterval(bufferMonitorInterval);
          }
          if (speedSuggestionInterval) {
            clearInterval(speedSuggestionInterval);
          }
          if (seekBufferPrimeTimer) {
            clearInterval(seekBufferPrimeTimer);
          }
          PlayerStore.setHlsInstanceRef(null);
          hls.destroy();
        };
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = url;
        video.currentTime = startTime;

        video.addEventListener('loadedmetadata', () => {
          video.play();
        });

        video.addEventListener('timeupdate', handleTimeUpdate);
        video.addEventListener('ended', handleEnded);
        video.addEventListener('seeked', handleSeeked);
        video.addEventListener('playing', handlePlaying);
        return () => {
          video.removeEventListener('timeupdate', handleTimeUpdate);
          video.removeEventListener('ended', handleEnded);
          video.removeEventListener('seeked', handleSeeked);
          video.removeEventListener('playing', handlePlaying);
        };
      }
    }, []);
    //[url , startTime, endTime, hlsConfig, onEndTime]
    useEffect(() => {
      const video = videoRef.current;

      if (playbackSpeed !== video.playbackRate) {
        // if the playback rate is 100.0, this means we are in smart mode, load as 1 to start...
        if (playbackSpeed === '100.0') {
          video.playbackRate = 1;
        } else {
          video.playbackRate = playbackSpeed;
        }

        if (playing && !isPlaying()) {
          // Check if video has a valid source before trying to play
          const hasValidSource = video.src || (hlsInstanceRef.current && hlsInstanceRef.current.media);
          if (hasValidSource) {
            video.play().catch((error) => {
              console.error('HlsVideoPlayer: Failed to play video after playback speed change:', error);
            });
          }
        }
      }

      // Dynamically scale HLS.js buffer config based on playback speed
      // Higher speeds consume buffer faster, so we need proportionally more buffer
      const hls = hlsInstanceRef.current;
      if (hls) {
        const speed = getEffectiveSpeed();

        // Scale forward buffer: at 1x=30s, at 2x=60s, at 5x=100s, at 10x=200s
        // Formula: min(300, max(30, speed × 20))
        const scaledMaxBuffer = Math.min(300, Math.max(30, speed * 20));
        const scaledMaxMaxBuffer = Math.min(600, Math.max(60, speed * 40));

        // Scale back buffer inversely: at 1x=30s, at 5x=10s, at 10x=5s
        // At high speeds we don't need much back buffer — memory is better used for forward buffer
        const scaledBackBuffer = Math.max(5, Math.round(30 / speed));

        // Scale front buffer flush threshold proportionally
        const scaledFlushThreshold = Math.min(300, Math.max(60, speed * 20));

        // Scale buffer size: at 1x=100MB, at 5x=200MB, at 10x=300MB
        const scaledBufferSize = Math.min(300, Math.max(100, speed * 30)) * 1000 * 1000;

        hls.config.maxBufferLength = scaledMaxBuffer;
        hls.config.maxMaxBufferLength = scaledMaxMaxBuffer;
        hls.config.backBufferLength = scaledBackBuffer;
        hls.config.frontBufferFlushThreshold = scaledFlushThreshold;
        hls.config.maxBufferSize = scaledBufferSize;

        console.log(`[HLS Buffer] Speed: ${speed}x → maxBuffer: ${scaledMaxBuffer}s, maxMaxBuffer: ${scaledMaxMaxBuffer}s, backBuffer: ${scaledBackBuffer}s, flushThreshold: ${scaledFlushThreshold}s, bufferSize: ${(scaledBufferSize / 1000000).toFixed(0)}MB`);
      }
    }, [playbackSpeed]);

    useEffect(() => {
      const video = videoRef.current;

      if (playing && !isPlaying()) {
        // If seekBufferPrime is actively holding playback, don't start playing yet.
        // seekBufferPrime will resume playback itself once sufficient buffer is available.
        if (seekBufferPrimingRef.current) {
          return;
        }

        // Check if video has a valid source before trying to play
        const hasValidSource = video.src || (hlsInstanceRef.current && hlsInstanceRef.current.media);

        if (!hasValidSource) {
          console.warn('HlsVideoPlayer: Cannot play - no valid source attached yet');
          return;
        }

        // Only capture audio stream at normal/low speeds (≤2x)
        // At high speeds audio is unintelligible and captureStream consumes GPU resources
        const effectiveSpeed = getEffectiveSpeed();
        if (effectiveSpeed <= 2) {
          try {
            // Reuse shared MediaStream or create one if it doesn't exist
            // This avoids creating multiple captureStream() calls from the same video element
            let mediaStream = PlayerStore.hlsMediaStream;
            if (!mediaStream || !mediaStream.active) {
              mediaStream = video.captureStream(30); // 30fps is sufficient for audio extraction
              PlayerStore.setHlsMediaStream(mediaStream);
            }
            const audioTrack = mediaStream.getAudioTracks()[0];

            // Only create LocalAudioTrack if audioTrack exists
            if (audioTrack) {
              const localAudio = new LocalAudioTrack(audioTrack, {
                name: 'hls-audio',
              });
              setAudioTrack(localAudio);
            }
          } catch (error) {
            console.warn('Failed to capture audio stream:', error);
          }
        } else if (audioTrack) {
          // Clean up audio track at high speeds to free resources
          setAudioTrack(null);
        }

        video.play().catch((error) => {
          console.error('HlsVideoPlayer: Failed to play video:', error);
        });
        //requestAnimationFrame(drawVideos);
      } else if (!playing && isPlaying()) {
        video.pause();
      }
    }, [playing]);

    return (
      <video
        ref={videoRef}
        style={showRawVideo ? { width: '100%', height: '100%' } : { height: '0px', width: '0px' }}
        {...videoProps}
      />
    );
  }
);

export default HlsVideoPlayer;
